import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";

// Georgia bounding box (lon/lat) rendered in Web Mercator by the Esri export, so data must be projected the same way.
// Full-bleed 16:9 window centred on Georgia (banner overlays the map, TV-station style).
const BBOX = { w: -89.16, e: -77.44, s: 30.2, n: 35.4 };
export const W = 1920, H = 1080;
const esri = (svc, fmt = "png32") => `https://server.arcgisonline.com/ArcGIS/rest/services/${svc}/MapServer/export?bbox=${BBOX.w},${BBOX.s},${BBOX.e},${BBOX.n}&bboxSR=4326&size=${W},${H}&imageSR=3857&format=${fmt}&transparent=true&f=image`;
const BASE = esri("Canvas/World_Light_Gray_Base");      // light land, blue-gray water
const RELIEF = esri("Elevation/World_Hillshade");        // grayscale shaded relief
const ROADS = esri("Reference/World_Transportation");    // interstates + shields

const merc = (lat) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
const MN = merc(BBOX.n), MS = merc(BBOX.s);
export const project = (lat, lon) => ({ x: ((lon - BBOX.w) / (BBOX.e - BBOX.w)) * W, y: ((MN - merc(lat)) / (MN - MS)) * H });

let countiesCache = null;
let countiesPromise = null;
if (typeof Image !== "undefined") { [BASE, RELIEF, ROADS].forEach((u) => { const im = new Image(); im.src = u; }); }
// Each county: { name, d (1920x1080 path), rings: [[lon,lat]...][] (outer rings), bbox: {w,s,e,n}, centroid: [lon,lat] }
export function loadCounties() {
  if (countiesCache) return Promise.resolve(countiesCache);
  if (!countiesPromise) {
    countiesPromise = api.get("/geo/ga-counties").then((r) => {
      const toPath = (rings) => rings.map((ring) => ring.map(([lon, lat], i) => { const p = project(lat, lon); return `${i ? "L" : "M"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`; }).join(" ") + "Z").join(" ");
      const out = (r.data.features || []).map((f) => {
        const g = f.geometry;
        const polys = g.type === "Polygon" ? [g.coordinates] : g.coordinates;
        const rings = polys.map((p) => p[0]);
        let w = 180, e = -180, s = 90, n = -90, sx = 0, sy = 0, cnt = 0;
        rings.forEach((ring) => ring.forEach(([lon, lat]) => { w = Math.min(w, lon); e = Math.max(e, lon); s = Math.min(s, lat); n = Math.max(n, lat); sx += lon; sy += lat; cnt++; }));
        return { name: f.properties?.NAME, d: g.type === "Polygon" ? toPath(g.coordinates) : g.coordinates.map(toPath).join(" "), rings, bbox: { w, s, e, n }, centroid: [sx / cnt, sy / cnt] };
      });
      countiesCache = out;
      return out;
    }).catch(() => []);
  }
  return countiesPromise;
}
export function useCounties() {
  const [paths, setPaths] = useState(countiesCache || []);
  useEffect(() => {
    if (countiesCache) return;
    let on = true;
    loadCounties().then((out) => { if (on) setPaths(out); });
    return () => { on = false; };
  }, []);
  return paths;
}

export function GrayBase({ children, counties, strokeWidth = 2 }) {
  return (
    <>
      <image href={BASE} x="0" y="0" width={W} height={H} preserveAspectRatio="none" style={{ filter: "saturate(5) hue-rotate(-18deg) brightness(0.86) contrast(1.15)" }} />
      <image href={RELIEF} x="0" y="0" width={W} height={H} preserveAspectRatio="none" opacity="0.9" style={{ mixBlendMode: "multiply", filter: "contrast(1.3)" }} />
      <rect x="0" y="0" width={W} height={H} fill="#5c6670" style={{ mixBlendMode: "multiply", opacity: 0.35 }} />
      {children}
      <image href={ROADS} x="0" y="0" width={W} height={H} preserveAspectRatio="none" opacity="0.95" />
      <g fill="none" stroke="#1c2430" strokeWidth={strokeWidth} strokeLinejoin="round" opacity="0.9" style={{ transition: "stroke-width 1400ms" }}>
        {counties.map((c) => <path key={c.name} d={c.d} />)}
      </g>
    </>
  );
}

// Major Georgia cities — a light geographic anchor for the radar / futurecast / outlook maps.
const MAJOR_CITIES = [
  { name: "Atlanta", lat: 33.749, lon: -84.388 },
  { name: "Savannah", lat: 32.0809, lon: -81.0912 },
  { name: "Augusta", lat: 33.4735, lon: -82.0105 },
  { name: "Columbus", lat: 32.461, lon: -84.9877 },
  { name: "Macon", lat: 32.8407, lon: -83.6324 },
  { name: "Albany", lat: 31.5785, lon: -84.1557 },
  { name: "Athens", lat: 33.9519, lon: -83.3576 },
  { name: "Valdosta", lat: 30.8327, lon: -83.2785 },
  { name: "Brunswick", lat: 31.15, lon: -81.4915 },
  { name: "Rome", lat: 34.257, lon: -85.1647 },
];

// Renders city dots + halo labels; text is counter-scaled so it stays readable when the map zooms in.
export function CityLabels({ scale = 1, fontSize = 27 }) {
  const k = 1 / scale;
  return (
    <g data-testid="city-labels" style={{ pointerEvents: "none" }}>
      {MAJOR_CITIES.map((c) => {
        const p = project(c.lat, c.lon);
        return (
          <g key={c.name} transform={`translate(${p.x.toFixed(1)} ${p.y.toFixed(1)}) scale(${k.toFixed(3)})`} data-testid={`city-label-${c.name}`}>
            <circle r="6" fill="#ffffff" stroke="#0a0f1c" strokeWidth="3" />
            <text x="13" y="9" fontFamily="Oswald, sans-serif" fontWeight="700" fontSize={fontSize} fill="#0a0f1c" stroke="#ffffff" strokeWidth="5" paintOrder="stroke" strokeLinejoin="round" style={{ textTransform: "uppercase", letterSpacing: "0.03em" }}>{c.name}</text>
          </g>
        );
      })}
    </g>
  );
}


// Smoothed TV-style radar frames rendered server-side (/api/radar/frame) on the same 1920x1080 Mercator bbox,
// from the IEM 4326 composites (N0Q ~500 m, HRRR precip-type) — far sharper than stretched zoom-7 tiles.
const frameUrl = (mode, key) => `${API}/radar/frame?mode=${mode}&key=${key}`;

// IEM mosaic archive is published at minute modulo 5; latest scan is usually ready ~5 min after valid time.
const pad = (n) => String(n).padStart(2, "0");
const iemStamp = (d) => `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}`;
export function liveLoopFrames(now = Date.now(), steps = 12) {
  const latest = Math.floor((now - 6 * 60000) / 300000) * 300000;
  return Array.from({ length: steps }, (_, k) => {
    const valid = new Date(latest - (steps - 1 - k) * 300000);
    const ago = (steps - 1 - k) * 5;
    return { key: iemStamp(valid), label: ago ? `-${ago} MIN` : "LATEST", valid };
  });
}

const FRAMES = {
  futurecast: ["0000", "0060", "0120", "0180", "0240", "0300", "0360"].map((m) => ({ key: m, minutes: Number(m), label: m === "0000" ? "NOW" : `+${Number(m) / 60} HR` })),
};

export function RadarLegend() {
  return (
    <div className="flex items-center gap-5 font-oswald text-[22px] font-bold uppercase tracking-wider text-white" data-testid="radar-legend">
      <span className="flex items-center gap-2"><span className="h-5 w-24 rounded-sm" style={{ background: "linear-gradient(90deg,#2fbf3f,#f6e41b,#f28d1c,#d7191c)" }} />Rain</span>
      <span className="flex items-center gap-2"><span className="h-5 w-12 rounded-sm" style={{ background: "linear-gradient(90deg,#f49ac1,#c51b8a)" }} />Mix</span>
      <span className="flex items-center gap-2"><span className="h-5 w-12 rounded-sm" style={{ background: "linear-gradient(90deg,#9ecae1,#3182bd,#6a51a3)" }} />Snow</span>
    </div>
  );
}

const ABBR = { north: "N", northeast: "NE", east: "E", southeast: "SE", south: "S", southwest: "SW", west: "W", northwest: "NW" };
function useStormMotion(enabled) {
  const [data, setData] = useState({ motion: null, arrivals: [], zoom: null });
  useEffect(() => {
    if (!enabled) return undefined;
    const load = () => api.get("/radar/counties").then((r) => setData({ motion: r.data?.motion || null, arrivals: r.data?.arrivals || [], zoom: r.data?.zoom || null })).catch(() => {});
    load();
    const t = setInterval(load, 4 * 60 * 1000);
    return () => clearInterval(t);
  }, [enabled]);
  return data;
}

// GOES-19 GLM flashes (last 15 min) — polled every 30 s while the live radar is up; 1 s tick ages the bolts.
function useLightning(enabled) {
  const [data, setData] = useState({ flashes: [], count_15m: 0, count_5m: 0 });
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    const load = () => api.get("/lightning").then((r) => setData(r.data || {})).catch(() => {});
    load();
    const t = setInterval(load, 30 * 1000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(t); clearInterval(tick); };
  }, [enabled]);
  return { ...data, now };
}

const BOLT = "M13 2 3 14h9l-1 8 10-12h-9l1-8z";
function LightningLayer({ flashes = [], now, scale = 1 }) {
  const recent = flashes.filter((f) => now / 1000 - f.t <= 300);
  if (!recent.length) return null;
  const k = 1.3 / scale;
  return (
    <g data-testid="lightning-layer" data-count={recent.length}>
      {recent.map((f, i) => {
        const age = now / 1000 - f.t;
        const p = project(f.lat, f.lon);
        const fresh = age < 75;
        return (
          <g key={`${f.t}-${i}`} transform={`translate(${p.x.toFixed(1)} ${p.y.toFixed(1)}) scale(${k.toFixed(3)}) translate(-12 -12)`} className={fresh ? "bolt-flash" : undefined} opacity={fresh ? 1 : Math.max(0.25, 1 - age / 300)}>
            <path d={BOLT} fill={fresh ? "#fff7b0" : "#ffd60a"} stroke="#0a0f1c" strokeWidth="1.6" strokeLinejoin="round" />
          </g>
        );
      })}
    </g>
  );
}

const esriBox = (svc, b) => `https://server.arcgisonline.com/ArcGIS/rest/services/${svc}/MapServer/export?bbox=${b.w},${b.s},${b.e},${b.n}&bboxSR=4326&size=${W},${H}&imageSR=3857&format=png32&transparent=true&f=image`;
// Hi-res basemap + towns for the storm-zoom region, drawn inside its rect so it's crisp once the map scales up.
function ZoomRegionLayer({ zoom, show, children }) {
  const { rect, bbox, scale } = zoom;
  return (
    <g opacity={show ? 1 : 0} style={{ transition: "opacity 600ms ease" }} data-testid="zoom-region-layer">
      <image href={esriBox("Canvas/World_Light_Gray_Base", bbox)} x={rect.x} y={rect.y} width={rect.w} height={rect.h} preserveAspectRatio="none" style={{ filter: "saturate(5) hue-rotate(-18deg) brightness(0.86) contrast(1.15)" }} />
      <image href={esriBox("Elevation/World_Hillshade", bbox)} x={rect.x} y={rect.y} width={rect.w} height={rect.h} preserveAspectRatio="none" opacity="0.9" style={{ mixBlendMode: "multiply", filter: "contrast(1.3)" }} />
      <rect x={rect.x} y={rect.y} width={rect.w} height={rect.h} fill="#5c6670" style={{ mixBlendMode: "multiply", opacity: 0.35 }} />
      {children}
      <image href={esriBox("Reference/World_Transportation", bbox)} x={rect.x} y={rect.y} width={rect.w} height={rect.h} preserveAspectRatio="none" opacity="0.95" />
      {zoom.towns.map((t) => {
        const p = project(t.lat, t.lon);
        return (
          <g key={t.name} transform={`translate(${p.x.toFixed(1)} ${p.y.toFixed(1)}) scale(${(1 / scale).toFixed(3)})`} data-testid={`zoom-town-${t.name}`}>
            <circle r="7" fill="#0a0f1c" stroke="#fff" strokeWidth="3" />
            <text x="14" y="9" fontFamily="Oswald, sans-serif" fontWeight="700" fontSize="26" fill="#0a0f1c" stroke="#fff" strokeWidth="5" paintOrder="stroke" style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.name}</text>
          </g>
        );
      })}
    </g>
  );
}

function ArrivalPanel({ arrivals }) {
  const rows = arrivals.slice(0, 8);
  if (!rows.length) return null;
  return (
    <div className="absolute right-[24px] top-[318px] w-[440px] overflow-hidden rounded-[3px] bg-white" data-testid="arrival-panel">
      <div className="flex items-center justify-between px-4 py-2 font-oswald text-[22px] font-black uppercase tracking-wider text-white" style={{ background: "#0a0f1c" }}>
        <span>Storm Arrival Times</span><span className="text-[15px] font-bold tracking-widest text-[#ffd60a]">Heavy rain</span>
      </div>
      {rows.map((a, i) => (
        <div key={a.town} className={`flex items-center justify-between px-4 py-[6px] font-oswald ${i % 2 ? "bg-[#eef1f5]" : "bg-white"}`} data-testid={`arrival-row-${i}`}>
          <span className="flex items-center gap-3 text-[24px] font-bold uppercase tracking-wide text-[#0a0f1c]">
            <span className="h-4 w-4 rounded-full border-2 border-[#0a0f1c]" style={{ background: a.dbz >= 50 ? "#d7191c" : "#f6e41b" }} />{a.town}
          </span>
          <span className={`text-[24px] font-black uppercase ${a.eta_min === 0 ? "text-[#d90429]" : "text-[#1f63c9]"}`}>{a.eta_min === 0 ? "Now" : `${a.eta_min} min`}</span>
        </div>
      ))}
    </div>
  );
}

export function TvRadarMap({ mode = "futurecast", durationMs = 15000 }) {
  const counties = useCounties();
  const live = mode === "live";
  const { motion, arrivals, zoom } = useStormMotion(live);
  const lightning = useLightning(live);
  const [stamp, setStamp] = useState(() => Date.now());
  const frames = live ? liveLoopFrames(stamp) : FRAMES[mode];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (frames.length < 2) return undefined;
    const t = setInterval(() => setI((n) => (n + 1) % frames.length), i === frames.length - 1 ? 1800 : live ? 450 : 800);
    return () => clearInterval(t);
  }, [frames.length, i, live]);
  useEffect(() => { const t = setInterval(() => setStamp(Date.now()), 5 * 60 * 1000); return () => clearInterval(t); }, []);
  // Storm zoom: statewide loop for the first ~45% of the slide, then push in on the hottest region.
  const [zoomed, setZoomed] = useState(false);
  const zoomId = live && zoom ? zoom.id : null;
  useEffect(() => {
    if (!zoomId) { setZoomed(false); return undefined; }
    const t = setTimeout(() => setZoomed(true), Math.max(4000, durationMs * 0.45));
    return () => clearTimeout(t);
  }, [zoomId, durationMs]);
  const now = new Date();
  const valid = mode === "futurecast" ? new Date(now.getTime() + frames[i].minutes * 60000) : frames[i].valid;
  const timeLabel = valid.toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" });
  const hrrrBust = Math.floor(stamp / (10 * 60 * 1000));
  const s = zoomed && zoom ? zoom.scale : 1;
  const c = zoomed && zoom ? zoom.center : { x: W / 2, y: H / 2 };
  const zoomTransform = `translate(${W / 2}px, ${H / 2}px) scale(${s}) translate(${-c.x}px, ${-c.y}px)`;
  const bolts = lightning.flashes || [];
  return (
    <div className="relative h-full w-full overflow-hidden bg-[#cfd3d8]" data-testid={`tv-radar-map-${mode}`} data-zoomed={zoomed ? zoom?.id : ""}>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full" preserveAspectRatio="xMidYMid slice">
        <g style={{ transform: zoomTransform, transformOrigin: "0 0", transition: "transform 1400ms cubic-bezier(.45,0,.2,1)" }} data-testid="radar-zoom-group">
          <GrayBase counties={counties} strokeWidth={2.4 / s}>
            {frames.map((f, k) => (
              <image
                key={f.key}
                href={live ? frameUrl("live", f.key) : `${frameUrl("futurecast", f.key)}&v=${hrrrBust}`}
                x="0" y="0" width={W} height={H} preserveAspectRatio="none"
                opacity={k === i ? 0.9 : 0}
                data-testid={k === i ? "radar-frame-active" : undefined}
              />
            ))}
            {zoom && live && (
              <ZoomRegionLayer zoom={zoom} show={zoomed}>
                {frames.map((f, k) => (
                  <image key={f.key} href={`${frameUrl("live", f.key)}&region=${zoom.id}`} x={zoom.rect.x} y={zoom.rect.y} width={zoom.rect.w} height={zoom.rect.h} preserveAspectRatio="none" opacity={k === i ? 0.9 : 0} />
                ))}
              </ZoomRegionLayer>
            )}
          </GrayBase>
          <g opacity={zoomed ? 0 : 1} style={{ transition: "opacity 500ms ease" }}>
            <CityLabels scale={s} />
          </g>
          {live && <LightningLayer flashes={bolts} now={lightning.now} scale={s} />}
        </g>
      </svg>
      <div className="absolute right-[24px] top-[170px] rounded-[3px] bg-white px-5 py-2 font-oswald text-[30px] font-black uppercase tracking-wide text-[#0a0f1c]" data-testid="radar-frame-label">
        {frames[i].label} <span className="ml-2 text-[#1f63c9]">{timeLabel}</span>
      </div>
      {live && (
        <div className="absolute right-[24px] top-[232px] flex h-[6px] w-[220px] overflow-hidden rounded-full bg-black/25" data-testid="radar-loop-progress">
          <div className="h-full bg-[#1f63c9]" style={{ width: `${((i + 1) / frames.length) * 100}%`, transition: "width 150ms linear" }} />
        </div>
      )}
      {live && motion && (
        <div className="absolute right-[24px] top-[256px] flex items-center gap-3 rounded-[3px] bg-[#0a0f1c] px-4 py-2 font-oswald text-[22px] font-bold uppercase tracking-wider text-white" data-testid="storm-motion-chip">
          {motion.speed_mph >= 5 ? (
            <>
              <svg viewBox="-12 -12 24 24" className="h-7 w-7" style={{ transform: `rotate(${motion.bearing}deg)` }}><path d="M0 -10 L7 2 L2 2 L2 10 L-2 10 L-2 2 L-7 2 Z" fill="#ffd60a" /></svg>
              <span>Storm motion · {ABBR[motion.toward]} {motion.speed_mph} mph</span>
            </>
          ) : <span>Storms nearly stationary</span>}
        </div>
      )}
      {live && <ArrivalPanel arrivals={arrivals} />}
      {live && zoomed && zoom && (
        <div className="zoom-tag-in absolute left-[24px] top-[210px] flex items-center gap-3 rounded-[3px] bg-[#d90429] px-5 py-2 font-oswald text-[28px] font-black uppercase tracking-wider text-white shadow-[0_8px_24px_rgba(0,0,0,0.35)]" data-testid="storm-zoom-tag">
          <span className="live-dot" style={{ width: 10, height: 10 }} /> Storm zoom · {zoom.name}
          <span className="ml-2 rounded-[2px] bg-white/20 px-2 py-0.5 text-[18px] font-bold tracking-widest">{Math.round(zoom.max_dbz)} dBZ</span>
        </div>
      )}
      {live && lightning.count_15m > 0 && (
        <div className="absolute bottom-[96px] left-[24px] flex items-center gap-3 rounded-[3px] bg-[#0a0f1c] px-4 py-2 font-oswald text-[22px] font-bold uppercase tracking-wider text-white" data-testid="lightning-chip">
          <svg viewBox="0 0 24 24" className="h-7 w-7 bolt-flash"><path d={BOLT} fill="#ffd60a" stroke="#ffd60a" strokeWidth="1" strokeLinejoin="round" /></svg>
          <span>{lightning.count_15m.toLocaleString()} {lightning.count_15m === 1 ? "strike" : "strikes"} · last 15 min</span>
          {lightning.count_5m > 0 && <span className="rounded-[2px] bg-[#ffd60a] px-2 py-0.5 text-[16px] font-black tracking-widest text-[#0a0f1c]">{lightning.count_5m} in 5 min</span>}
        </div>
      )}
      <div className="absolute bottom-[64px] left-[24px] font-oswald text-[14px] uppercase tracking-widest text-[#0a0f1c]/80">
        {mode === "futurecast" ? "HRRR model · simulated reflectivity · Iowa Env. Mesonet" : `NWS NEXRAD composite · past 60 min · Iowa Env. Mesonet${lightning.count_15m > 0 ? " · Lightning: NOAA GOES-19 GLM" : ""}`}
      </div>
    </div>
  );
}


const GUST_BANDS = [
  { min: 40, label: "Damaging 40+", color: "#d90429" },
  { min: 30, label: "Gusty 30+", color: "#f28d1c" },
  { min: 20, label: "Breezy 20+", color: "#f6e41b" },
  { min: 0, label: "Calm", color: "#7bd389" },
];
const gustBand = (v) => GUST_BANDS.find((b) => v >= b.min) || GUST_BANDS[GUST_BANDS.length - 1];

export function WindLegend() {
  return (
    <div className="flex items-center gap-5 font-oswald text-[22px] font-bold uppercase tracking-wider text-white" data-testid="wind-legend">
      {[...GUST_BANDS].reverse().map((b) => (
        <span key={b.label} className="flex items-center gap-2"><span className="h-5 w-5 rounded-full border-2 border-white/80" style={{ background: b.color }} />{b.label}</span>
      ))}
    </div>
  );
}

// Plots observed gusts (mph) per city; arrow points where the wind is blowing TO (NWS dir is "from").
export function TvWindMap({ points }) {
  const counties = useCounties();
  return (
    <div className="relative h-full w-full overflow-hidden bg-[#cfd3d8]" data-testid="tv-wind-map">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full" preserveAspectRatio="xMidYMid slice">
        <GrayBase counties={counties} />
        {points.map((p) => {
          const { x, y } = project(p.lat, p.lon);
          const has = typeof p.value === "number";
          const band = gustBand(has ? p.value : 0);
          return (
            <g key={p.id} data-testid={`tv-wind-point-${p.id}`}>
              {has && p.value >= 3 && p.dir != null && (
                <g transform={`translate(${x} ${y - 30}) rotate(${(p.dir + 180) % 360})`} data-testid={`tv-wind-arrow-${p.id}`}>
                  <path d="M0 -100 L16 -74 L6 -74 L6 -44 L-6 -44 L-6 -74 L-16 -74 Z" fill={band.color} stroke="#0a0f1c" strokeWidth="4" strokeLinejoin="round" />
                </g>
              )}
              <circle cx={x} cy={y - 30} r="40" fill={band.color} stroke="#0a0f1c" strokeWidth="5" />
              <text x={x} y={y - 15} textAnchor="middle" fontFamily="Oswald, sans-serif" fontWeight="800" fontSize="44" fill="#0a0f1c">{has ? Math.round(p.value) : "—"}</text>
              <text x={x} y={y + 40} textAnchor="middle" fontFamily="Oswald, sans-serif" fontWeight="700" fontSize="24" letterSpacing="1" fill="#fff" stroke="#000" strokeWidth="5" paintOrder="stroke" style={{ textTransform: "uppercase" }}>{p.name}</text>
            </g>
          );
        })}
      </svg>
      <div className="absolute bottom-[64px] left-[24px] font-oswald text-[14px] uppercase tracking-widest text-[#0a0f1c]/80">
        Peak gusts in MPH · NWS ASOS observations · arrows show wind flow
      </div>
    </div>
  );
}

// -------------------- Aviation flight conditions --------------------
export const FLT_CAT = {
  VFR:  { color: "#16a34a", label: "VFR" },
  MVFR: { color: "#2563eb", label: "MVFR" },
  IFR:  { color: "#dc2626", label: "IFR" },
  LIFR: { color: "#c026d3", label: "LIFR" },
};
const FLT_ORDER = ["VFR", "MVFR", "IFR", "LIFR"];

export function AviationLegend() {
  return (
    <div className="flex items-center gap-5 font-oswald text-[22px] font-bold uppercase tracking-wider text-white" data-testid="aviation-legend">
      {FLT_ORDER.map((k) => (
        <span key={k} className="flex items-center gap-2"><span className="h-5 w-5 rounded-full border-2 border-white/80" style={{ background: FLT_CAT[k].color }} />{FLT_CAT[k].label}</span>
      ))}
    </div>
  );
}

// Plots each Georgia airport as a color-coded flight-category dot with a wind-flow arrow.
export function TvAviationMap({ airports = [] }) {
  const counties = useCounties();
  return (
    <div className="relative h-full w-full overflow-hidden bg-[#cfd3d8]" data-testid="tv-aviation-map">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full" preserveAspectRatio="xMidYMid slice">
        <GrayBase counties={counties} />
        {airports.map((a) => {
          if (typeof a.lat !== "number" || typeof a.lon !== "number") return null;
          const { x, y } = project(a.lat, a.lon);
          const cat = FLT_CAT[a.cat] || { color: "#6b7280", label: a.cat || "—" };
          const code = (a.id || "").replace(/^K/, "");
          const windy = typeof a.wind_mph === "number" && a.wind_mph >= 3 && a.wind_dir != null;
          return (
            <g key={a.id} data-testid={`tv-aviation-point-${a.id}`}>
              {windy && (
                <g transform={`translate(${x} ${y - 34}) rotate(${(a.wind_dir + 180) % 360})`} data-testid={`tv-aviation-wind-${a.id}`}>
                  <path d="M0 -92 L14 -68 L5 -68 L5 -40 L-5 -40 L-5 -68 L-14 -68 Z" fill="#0a0f1c" stroke="#fff" strokeWidth="3" strokeLinejoin="round" />
                </g>
              )}
              <circle cx={x} cy={y} r="34" fill={cat.color} stroke="#fff" strokeWidth="5" />
              <circle cx={x} cy={y} r="34" fill="none" stroke="#0a0f1c" strokeWidth="2" />
              <text x={x} y={y + 9} textAnchor="middle" fontFamily="Oswald, sans-serif" fontWeight="800" fontSize="26" fill="#fff">{code}</text>
              <text x={x} y={y + 64} textAnchor="middle" fontFamily="Oswald, sans-serif" fontWeight="700" fontSize="24" letterSpacing="1" fill="#fff" stroke="#000" strokeWidth="5" paintOrder="stroke" style={{ textTransform: "uppercase" }}>{a.name}</text>
            </g>
          );
        })}
      </svg>
      <div className="absolute bottom-[64px] left-[24px] font-oswald text-[14px] uppercase tracking-widest text-[#0a0f1c]/80">
        Flight category by airport · NOAA Aviation Weather Center METARs · arrows show wind flow
      </div>
    </div>
  );
}

export function TvValueMap({ points, suffix = "" }) {
  const counties = useCounties();
  return (
    <div className="relative h-full w-full overflow-hidden bg-[#cfd3d8]" data-testid="tv-value-map">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full" preserveAspectRatio="xMidYMid slice">
        <GrayBase counties={counties} />
        {points.map((p) => {
          const { x, y } = project(p.lat, p.lon);
          const label = typeof p.value === "number" ? `${Math.round(p.value)}${suffix}` : "—";
          return (
            <g key={p.id} data-testid={`tv-map-point-${p.id}`}>
              <text x={x} y={y - 6} textAnchor="middle" fontFamily="Oswald, sans-serif" fontWeight="800" fontSize="68" fill="#fff" stroke="#000" strokeWidth="8" paintOrder="stroke" strokeLinejoin="round">{label}</text>
              <text x={x} y={y + 24} textAnchor="middle" fontFamily="Oswald, sans-serif" fontWeight="700" fontSize="24" letterSpacing="1" fill="#fff" stroke="#000" strokeWidth="5" paintOrder="stroke" style={{ textTransform: "uppercase" }}>{p.name}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// -------------------- SPC Convective Outlook --------------------
const SPC_COLORS = { TSTM: "#c1e9c1", MRGL: "#66a366", SLGT: "#ffe066", ENH: "#ffa366", MDT: "#e06666", HIGH: "#ee99ee" };
const SPC_STROKES = { TSTM: "#55bb55", MRGL: "#005500", SLGT: "#dda800", ENH: "#ff6600", MDT: "#cc0000", HIGH: "#aa00aa" };
const SPC_SHORT = { TSTM: "Thunder", MRGL: "Marginal", SLGT: "Slight", ENH: "Enhanced", MDT: "Moderate", HIGH: "High" };
const SPC_ORDER = ["TSTM", "MRGL", "SLGT", "ENH", "MDT", "HIGH"];

const ringPath = (ring) => ring.map(([lon, lat], i) => { const p = project(lat, lon); return `${i ? "L" : "M"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`; }).join(" ") + "Z";
export const geomPath = (g) => !g ? "" : g.type === "Polygon" ? g.coordinates.map(ringPath).join(" ") : g.coordinates.map((poly) => poly.map(ringPath).join(" ")).join(" ");

const spcDate = (s) => (s ? new Date(Date.UTC(+s.slice(0, 4), +s.slice(4, 6) - 1, +s.slice(6, 8), +s.slice(8, 10), +s.slice(10, 12))) : null);
export const spcDayLabel = (d) => {
  // Outlook periods run 12Z–12Z; the calendar day is the start of the period (Day 2/3 valid stamps are the 12Z start).
  const v = spcDate(d.valid);
  if (!v) return `Day ${d.day}`;
  const dayStart = new Date(v.getTime() - (v.getUTCHours() < 12 ? 12 * 3600000 : 0));
  const todayStr = new Date().toLocaleDateString("en-US", { timeZone: "America/New_York" });
  const name = dayStart.toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long" });
  const isToday = dayStart.toLocaleDateString("en-US", { timeZone: "America/New_York" }) === todayStr;
  return `Day ${d.day} · ${isToday ? "Today" : name}`;
};

export function SpcLegend({ labels }) {
  const show = SPC_ORDER.filter((l) => !labels || labels.includes(l));
  return (
    <div className="flex items-center gap-4 font-oswald text-[22px] font-bold uppercase tracking-wider text-white" data-testid="spc-legend">
      {show.map((l) => (
        <span key={l} className="flex items-center gap-2"><span className="h-5 w-7 rounded-sm border-2" style={{ background: SPC_COLORS[l], borderColor: SPC_STROKES[l] }} />{SPC_SHORT[l]}</span>
      ))}
    </div>
  );
}

export function TvOutlookMap({ day }) {
  const counties = useCounties();
  const feats = [...(day.features || [])].sort((a, b) => SPC_ORDER.indexOf(a.label) - SPC_ORDER.indexOf(b.label));
  const risk = day.ga_risk;
  return (
    <div className="relative h-full w-full overflow-hidden bg-[#cfd3d8]" data-testid={`tv-outlook-map-day${day.day}`}>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full" preserveAspectRatio="xMidYMid slice">
        <GrayBase counties={counties}>
          {feats.map((f) => (
            <path key={f.label} d={geomPath(f.geometry)} fill={SPC_COLORS[f.label]} fillOpacity="0.72" stroke={SPC_STROKES[f.label]} strokeWidth="4" strokeLinejoin="round" fillRule="evenodd" data-testid={`spc-poly-${f.label}`} />
          ))}
        </GrayBase>
        <CityLabels />
      </svg>
      <div className="absolute right-[24px] top-[170px] flex items-center gap-4 rounded-[3px] bg-white px-5 py-2 font-oswald text-[30px] font-black uppercase tracking-wide text-[#0a0f1c]" data-testid="spc-day-chip">
        <span>{spcDayLabel(day)}</span>
        <span className="rounded-[3px] px-3 py-0.5 text-[24px]" style={{ background: SPC_COLORS[risk] || "#e5e7eb", border: `3px solid ${SPC_STROKES[risk] || "#9ca3af"}` }}>
          {!risk ? "No Severe Risk · Georgia" : risk === "TSTM" ? "General Thunder · Georgia" : `${SPC_SHORT[risk]} Risk · Georgia`}
        </span>
      </div>
      <div className="absolute bottom-[64px] left-[24px] font-oswald text-[14px] uppercase tracking-widest text-[#0a0f1c]/80">
        NOAA Storm Prediction Center · categorical convective outlook
      </div>
    </div>
  );
}
